import React, { useState } from 'react';
import { CalendarClock, Mail, Send, X } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';

export default function AiEmailDrawer({
  open,
  onClose,
  recipientName,
  recipientEmail,
  subject,
  message,
  templateKey,
  templateOptions,
  tone,
  toneOptions,
  onToneChange,
  onTemplateChange,
  onSubjectChange,
  onMessageChange,
  onRegenerate,
  onSendNow,
  onSchedule,
  isRegenerating,
  isSending,
  smtpConnected = false,
  smtpFromEmail = null,
  smtpFromName = null,
  smtpStatusLoading = false,
  profilePath = '/builder/profile'
}) {
  const [mode, setMode] = useState('send');

  React.useEffect(() => {
    if (open) setMode('send');
  }, [open]);

  if (!open) return null;

  const canSend =
    smtpConnected &&
    !!recipientEmail &&
    !!message?.trim() &&
    !!subject?.trim() &&
    !isSending;

  return (
    <div className="fixed inset-0 z-[1200] flex justify-end bg-black/30">
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Email</h3>
            <p className="text-xs text-gray-500">AI-framed follow-up email</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-4 rounded-xl bg-gray-50 p-3">
            <p className="text-sm font-semibold text-gray-900">{recipientName || 'Lead'}</p>
            {recipientEmail ? (
              <p className="text-sm text-teal-700">{recipientEmail}</p>
            ) : (
              <p className="text-sm text-amber-700">No email on file for this lead.</p>
            )}
          </div>

          {smtpStatusLoading ? (
            <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Checking your Gmail connection…
            </div>
          ) : smtpConnected ? (
            <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900">
              Sending as{' '}
              <strong>
                {smtpFromName ? `${smtpFromName} <${smtpFromEmail}>` : smtpFromEmail}
              </strong>
            </div>
          ) : (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Connect your Gmail in{' '}
              <a href={profilePath} className="font-semibold underline">
                Profile
              </a>{' '}
              to send AI Insights emails from your inbox.
            </div>
          )}

          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setMode('send')}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold ${
                mode === 'send'
                  ? 'bg-[#1E4A53] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              <Send className="h-3.5 w-3.5" />
              Send now
            </button>
            <button
              type="button"
              onClick={() => setMode('schedule')}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold ${
                mode === 'schedule'
                  ? 'bg-[#1E4A53] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              <CalendarClock className="h-3.5 w-3.5" />
              Schedule send
            </button>
          </div>

          <div className="mb-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Tone</p>
            <div className="flex flex-wrap gap-2">
              {toneOptions?.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onToneChange?.(option.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                    tone === option.id
                      ? 'bg-[#1E4A53] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Follow-up type
            </label>
            <select
              value={templateKey}
              onChange={(e) => onTemplateChange?.(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {templateOptions?.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Subject
            </label>
            <input
              value={subject}
              onChange={(e) => onSubjectChange?.(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => onMessageChange?.(e.target.value)}
              rows={10}
              disabled={isRegenerating}
              className="w-full rounded-xl border border-gray-300 px-3 py-3 text-sm leading-relaxed disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 border-t px-5 py-4">
          <Button
            displayType="secondary"
            type="button"
            onClick={onRegenerate}
            disabled={isRegenerating}>
            Regenerate
          </Button>
          {mode === 'send' ? (
            <Button
              type="button"
              onClick={onSendNow}
              disabled={!canSend}
              className="flex-1 !inline-flex !items-center !justify-center !gap-2">
              <Mail className="h-4 w-4" />
              {isSending ? 'Sending…' : 'Send email'}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={onSchedule}
              disabled={
                !smtpConnected || !recipientEmail || !message?.trim() || !subject?.trim()
              }
              className="flex-1 !inline-flex !items-center !justify-center !gap-2">
              <CalendarClock className="h-4 w-4" />
              Pick date & time
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
